import axios from "axios";
import { useSelector } from "react-redux";
import RecordRTC from "recordrtc";
import { useCreateMediaStream } from "./useCreateMediaStream";
import { useRef, useState } from "react";
import { getCurrentDate } from "../common/getDateFormat";
import { socketId } from "../data/socketID";
import { UPLOAD_RECORD } from "../fetch/apiConfig";
import { uploadRecordFileWithFallback } from "../common/uploadToS3";

//녹화 상태를 담을 배열
let recorder_video;
let recorder_screen;

const RECORD_UPLOAD_REGISTER_RETRY_COUNT = 3;
const RECORD_UPLOAD_REGISTER_RETRY_DELAY_MS = 1000;
const RECORD_UPLOAD_FAILURE_LOG_KEY = "recordUploadFailureLogs";

const getRecordTimestamp = () => {
  const rawDate = getCurrentDate(new Date());

  return `${rawDate.slice(0, 4)}_${rawDate.slice(4, 6)}_${rawDate.slice(
    6,
    8
  )}_${rawDate.slice(8, 10)}_${rawDate.slice(10, 12)}_${rawDate.slice(
    12,
    14
  )}`;
};

const wait = (delay) =>
  new Promise((resolve) => {
    setTimeout(resolve, delay);
  });

const saveRecordUploadFailureLog = ({ recordType, recordUrl, error }) => {
  const logMessage = `[UPLOAD_RECORD failed] type=${recordType}, path=${recordUrl}`;

  console.error(logMessage, error);

  if (typeof window === "undefined") {
    return;
  }

  try {
    const previousLogs = JSON.parse(
      window.localStorage.getItem(RECORD_UPLOAD_FAILURE_LOG_KEY) || "[]"
    );

    previousLogs.push({
      message: logMessage,
      recordType,
      recordUrl,
      errorMessage: error?.message || String(error),
      createdAt: new Date().toISOString(),
    });

    window.localStorage.setItem(
      RECORD_UPLOAD_FAILURE_LOG_KEY,
      JSON.stringify(previousLogs.slice(-20))
    );
  } catch (storageError) {
    console.error("Failed to persist record upload failure log", storageError);
  }
};

export const useRecord = () => {
  const userInfo = useSelector((state) => state.userInfo);
  const [loading, setLoading] = useState(false);

  const { getWebcam } = useCreateMediaStream();
  const { videoStream, screenStream } = useSelector((state) => state.device);
  const { myRoomId, myPeerId } = socketId(
    userInfo?.ROOM_IDX,
    userInfo?.Tester_IDX
  );
  const examSubStatusRef = useRef(null);

  const registerUploadedRecord = async (recordType, recordUrl) => {
    let lastError;

    for (
      let attempt = 1;
      attempt <= RECORD_UPLOAD_REGISTER_RETRY_COUNT;
      attempt += 1
    ) {
      try {
        await axios.post(UPLOAD_RECORD, {
          recordType,
          recordUrl,
        });

        return;
      } catch (error) {
        lastError = error;

        if (attempt === RECORD_UPLOAD_REGISTER_RETRY_COUNT) {
          break;
        }

        console.warn(
          `UPLOAD_RECORD retry ${attempt}/${RECORD_UPLOAD_REGISTER_RETRY_COUNT} failed`,
          error
        );
        await wait(RECORD_UPLOAD_REGISTER_RETRY_DELAY_MS);
      }
    }

    throw lastError;
  };

  const uploadRecordFile = async (fileName, blob, recordType) => {
    const file = new File([blob], fileName, {
      type: "video/webm",
    });

    const recordUrl = await uploadRecordFileWithFallback({
      file,
      fileName,
      peerId: myPeerId,
      roomId: myRoomId,
    });

    try {
      await registerUploadedRecord(recordType, recordUrl);
    } catch (error) {
      saveRecordUploadFailureLog({
        recordType,
        recordUrl,
        error,
      });
      throw error;
    }
  };

  /** 웹캠 파일 저장 */
  const postFilesVideo = async () => {
    try {
      const d = getRecordTimestamp();
      const fileName = `[cam]${myPeerId}_video_${d}.webm`;
      const blob = recorder_video.getBlob();
      await uploadRecordFile(fileName, blob, "CAM");

      startCamRecordingHandler(videoStream);
    } catch (error) {
      startCamRecordingHandler(videoStream);
    }
  };

  const sendVideoFile = async () => {
    try {
      const d = getRecordTimestamp();
      const fileName = `[cam]${myPeerId}_video_${d}.webm`;
      const blob = recorder_video.getBlob();
      await uploadRecordFile(fileName, blob, "CAM");
    } catch {}
  };
  /** 스크린 파일 저장 포기 조건없이 보냄*/
  const sendAllScreenWabCamFile = async () => {
    try {
      const d = getRecordTimestamp();
      const fileName = `[screen]${myPeerId}_screen_${d}.webm`;
      const blob = recorder_screen.getBlob();
      await uploadRecordFile(fileName, blob, "SCREEN");

      setLoading(false);
      recorder_video?.stopRecording(postFilesVideo);
      startScreenRecordingHandler(screenStream);
    } catch {
      startScreenRecordingHandler(screenStream);
      setLoading("error");
    }
  };
  /** 스크린 파일 저장(포기 추가)*/
  const sendScreenWebCamFile = async () => {
    try {
      const d = getRecordTimestamp();
      const fileName = `[screen]${myPeerId}_screen_${d}.webm`;
      const blob = recorder_screen.getBlob();
      await uploadRecordFile(fileName, blob, "SCREEN");

      //예제 시간이 아닐 경우 캠 저장 포기
      if (examSubStatusRef.current === 1) {
        //포기
        startCamRecordingHandler(videoStream);
      } else {
        recorder_video?.stopRecording(postFilesVideo);
      }
      startScreenRecordingHandler(screenStream);
      setLoading(false);
    } catch {
      startScreenRecordingHandler(screenStream);
      setLoading("error");
    }
  };

  const startScreenRecordingHandler = async (screenStream) => {
    if (screenStream?.active) {
      recorder_screen = await RecordRTC(screenStream, {
        type: "video",
        disableLogs: true,
      });

      await recorder_screen?.startRecording();
    }
  };

  const startCamRecordingHandler = async (videoStream) => {
    if (videoStream?.active) {
      recorder_video = await RecordRTC(videoStream, {
        type: "video",
        disableLogs: true,
        videoBitsPerSecond: 10000,
      });

      await recorder_video?.startRecording();
    }
  };

  /** 녹화 중지 */
  const stopRecordHandler = async (sendAll = false) => {
    if (sendAll) {
      await recorder_screen?.stopRecording(sendAllScreenWabCamFile);
    } else {
      await recorder_screen?.stopRecording(sendScreenWebCamFile);
    }
  };

  const changeDeviceStartRecord = async () => {
    //녹화 중지, 녹화 전송
    await recorder_video?.stopRecording(sendVideoFile);
    //캠 바꿈
    getWebcam(async (stream) => {
      await startCamRecordingHandler(stream);
      return stream;
    });
  };

  /** 녹화 시작 */
  const startRecord = async () => {
    if (recorder_screen) {
      await stopRecordHandler();
    } else {
      startCamRecordingHandler(videoStream);
      startScreenRecordingHandler(screenStream);
    }
  };

  return {
    startRecord,
    stopRecordHandler,
    changeDeviceStartRecord,
    loading,
    setLoading,
  };
};
